import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Clock, Check, Sparkles, Sun, Heart, Share2,
  BookOpen, MessageCircle, NotebookPen, Activity, Bookmark, Crown, Settings as SettingsIcon,
  HeartHandshake, Users,
} from "lucide-react";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HOME, AFFIRMATION } from "@/constants/testIds";
import NowPlaying from "@/components/NowPlaying";
import { COLLECTION_IMAGES } from "@/constants/categoryImages";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const TOUR = [
  { icon: BookOpen, t: "Sisterhood University", d: "Short, real lessons on confidence, boundaries, motherhood, money, and more." },
  { icon: MessageCircle, t: "Your AI Coach", d: "A warm, wise sister in your corner — anytime, judgment-free." },
  { icon: Sparkles, t: "Daily Affirmation", d: "A personalized affirmation each day, right here on your home screen." },
  { icon: NotebookPen, t: "Journal & Wellness", d: "Private reflection and gentle check-ins to track how you're really doing." },
  { icon: Heart, t: "Community Circles", d: "A safe, moderated space to share and be held by other sisters." },
];
import { ACCENTS } from "@/constants/accents";

const HERO =
  "https://images.unsplash.com/photo-1579017308347-e53e0d2fc5e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const QUICK = [
  { key: "learn", label: "Learn", icon: BookOpen, to: "/learn" },
  { key: "coach", label: "Coach", icon: MessageCircle, to: "/coach" },
  { key: "journal", label: "Journal", icon: NotebookPen, to: "/journal" },
  { key: "wellness", label: "Wellness", icon: Activity, to: "/wellness" },
  { key: "saved", label: "Saved", icon: Bookmark, to: "/learn" },
  { key: "upgrade", label: "Upgrade", icon: Crown, to: "/paywall" },
  { key: "settings", label: "Settings", icon: SettingsIcon, to: "/settings" },
];

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aff, setAff] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [recPlan, setRecPlan] = useState(null);
  const [collJourney, setCollJourney] = useState(null);
  const [showTour, setShowTour] = useState(() => localStorage.getItem("sh_tour_seen") !== "1");
  const dismissTour = () => { localStorage.setItem("sh_tour_seen", "1"); setShowTour(false); };

  useEffect(() => {
    track("home_viewed");
    api.get("/home").then(({ data }) => setData(data)).catch(() => {});
    api.get("/affirmations/today").then(({ data }) => setAff(data)).catch(() => {});
    api.get("/assessment/status").then(({ data }) => setAssessment(data?.latest || null)).catch(() => {});
  }, []);

  useEffect(() => {
    const pref = user?.preferred_collection;
    if (!pref || pref === "all") { setCollJourney(null); return; }
    Promise.all([api.get("/categories"), api.get("/lessons")])
      .then(([{ data: cats }, { data: lessons }]) => {
        const collCats = cats.filter((c) => c.collection === pref);
        if (!collCats.length) { setCollJourney(null); return; }
        const firstCat = collCats[0];
        const catLessons = lessons.filter((l) => l.category_id === firstCat.id);
        const nextLesson = catLessons.find((l) => !l.completed) || catLessons[0];
        if (!nextLesson) { setCollJourney(null); return; }
        setCollJourney({
          collectionId: pref,
          collectionTitle: firstCat.collection_title,
          courseTitle: firstCat.title,
          lessonId: nextLesson.id,
          lessonTitle: nextLesson.title,
          resumed: !!(catLessons.length && catLessons.some((l) => l.completed)),
        });
      })
      .catch(() => setCollJourney(null));
  }, [user]);

  useEffect(() => {
    if (!assessment?.recommended_plan_id) return;
    api.get("/plans").then(({ data }) => {
      setRecPlan((data || []).find((x) => x.id === assessment.recommended_plan_id) || null);
    }).catch(() => {});
  }, [assessment]);

  const toggleSaveAff = async () => {
    if (!aff) return;
    const { data } = await api.post(`/affirmations/${aff.affirmation_id}/save`);
    setAff((a) => ({ ...a, saved: data.saved }));
    toast.success(data.saved ? "Saved to your collection." : "Removed from your collection.");
  };
  const shareAff = async () => {
    if (!aff) return;
    await api.post(`/affirmations/${aff.affirmation_id}/share`);
    toast.success("Shared to the Confidence circle 💛");
  };

  const rec = data?.recommendation;
  const stats = data?.stats;
  const pct = stats?.total_lessons ? Math.round((stats.lessons_completed / stats.total_lessons) * 100) : 0;

  return (
    <div className="space-y-8">
      {/* Greeting */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C89B55] font-semibold flex items-center gap-2">
            <Sun className="h-3.5 w-3.5" /> {greeting()}
          </p>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1.5">{user?.name}.</h1>
        </div>
      </div>

      {/* Trial progress nudge — celebrate what they've done, then invite them to stay */}
      {user?.is_trial_active && user?.access && (() => {
        const done = [
          { icon: BookOpen, n: stats?.lessons_completed ?? 0, label: "lessons" },
          { icon: NotebookPen, n: stats?.journal_entries ?? 0, label: "reflections" },
          { icon: Activity, n: stats?.wellness_checkins ?? 0, label: "check-ins" },
          { icon: MessageCircle, n: user.access.trial_ai_used ?? 0, label: "coach chats" },
        ];
        const total = done.reduce((a, d) => a + d.n, 0);
        const days = user.access.trial_days_left ?? 0;
        return (
          <motion.div data-testid="home-trial-nudge"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className="rounded-3xl border border-[#C89B55]/40 bg-[#FBF4E7] p-6 lg:p-7">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#C89B55]" />
              <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">
                {days} day{days === 1 ? "" : "s"} left in your trial
              </p>
            </div>
            <h2 className="font-display text-2xl font-semibold mt-2 text-[#291B2D]">
              {total > 0 ? "Look how far you've come." : "Your growth starts here."}
            </h2>
            <p className="text-sm text-[#7A5A1E] mt-1">
              {total > 0 ? "Keep this going — subscribe so nothing you've built slips away." : "Explore everything free for 7 days, then subscribe to make it yours."}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              {done.map((d, i) => (
                <div key={i} className="rounded-2xl bg-white/70 border border-[#C89B55]/20 p-3 text-center">
                  <d.icon className="h-4 w-4 text-[#C89B55] mx-auto" />
                  <p className="font-display text-2xl font-semibold text-[#291B2D] mt-1">{d.n}</p>
                  <p className="text-[11px] text-[#7A5A1E]/80">{d.label}</p>
                </div>
              ))}
            </div>
            <button data-testid="home-trial-subscribe" onClick={() => navigate("/paywall")}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] px-6 py-3 font-semibold transition-smooth">
              <Crown className="h-4 w-4" /> Make Sisterhood+ mine
            </button>
          </motion.div>
        );
      })()}

      {/* Personalized collection journey — drops the member into their chosen path */}
      {collJourney && (
        <motion.div data-testid="home-collection-journey"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="relative overflow-hidden rounded-3xl text-[#FCFAF6] shadow-[0_10px_40px_rgba(41,27,45,0.18)]"
          style={{ backgroundColor: "#291B2D" }}>
          {COLLECTION_IMAGES[collJourney.collectionId] && (
            <img src={COLLECTION_IMAGES[collJourney.collectionId]} alt="" loading="lazy"
              className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "50% 30%" }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#291B2D] via-[#291B2D]/90 to-[#291B2D]/40" />
          <div className="relative z-10 p-7 lg:p-9 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Your journey</p>
            <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-2">
              {collJourney.resumed ? "Pick up where you left off" : `Welcome to ${collJourney.collectionTitle}`}
            </h2>
            <p className="text-[#F7F2EA]/85 mt-2">
              {collJourney.resumed ? "Continue" : "Begin"} with <span className="font-medium text-white">{collJourney.lessonTitle}</span> · {collJourney.courseTitle}
            </p>
            <div className="flex items-center gap-3 mt-6 flex-wrap">
              <button data-testid="home-collection-start-lesson" onClick={() => navigate(`/lesson/${collJourney.lessonId}`)}
                className="inline-flex items-center gap-2 bg-[#C89B55] text-[#291B2D] font-semibold rounded-full px-6 py-3 hover:brightness-105 transition-smooth">
                {collJourney.resumed ? "Continue lesson" : "Start here"} <ArrowRight className="h-4 w-4" />
              </button>
              <button data-testid="home-collection-explore" onClick={() => navigate("/learn")}
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white rounded-full px-5 py-2.5 text-sm transition-smooth">
                Explore all courses
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Signature music — subtle, brand-native Now Playing */}
      <NowPlaying />

      {/* Wellness score + recommended plan (from onboarding check-in) */}
      {assessment && (
        <motion.div data-testid="home-wellness-score"
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="rounded-3xl bg-white border border-[#E5DED4] p-7 lg:p-8 shadow-[0_4px_20px_rgba(41,27,45,0.04)]">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Your Wellness Score</p>
              <div className="flex items-end gap-2 mt-2">
                <span className="font-display text-5xl lg:text-6xl font-light text-[#291B2D] leading-none">{assessment.wellness_score}</span>
                <span className="text-[#8A838E] mb-1.5">/ 100</span>
              </div>
              {assessment.top_concern?.name && (
                <p className="text-sm text-[#59535B] mt-2">Gently nurturing <span className="font-medium text-[#291B2D]">{assessment.top_concern.name}</span>
                  {assessment.strength?.name && <> · strongest in <span className="font-medium text-[#291B2D]">{assessment.strength.name}</span></>}
                </p>
              )}
            </div>
            <button data-testid="home-wellness-view" onClick={() => navigate("/wellness")}
              className="rounded-full border border-[#E5DED4] hover:bg-[#F7F2EA] px-5 py-2.5 text-sm font-medium transition-smooth">
              View wellness
            </button>
          </div>
          {recPlan && (
            <button data-testid="home-recommended-plan" onClick={() => navigate(`/plans/${recPlan.id}`)}
              className="mt-5 w-full flex items-center justify-between rounded-2xl bg-[#F7F2EA] hover:bg-[#EFE7DA] transition-smooth p-5 text-left">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#B97978] font-semibold">Recommended for you</p>
                <p className="font-display text-lg mt-1">{recPlan.title}</p>
                {recPlan.subtitle && <p className="text-sm text-[#59535B] mt-0.5">{recPlan.subtitle}</p>}
              </div>
              <span className="h-11 w-11 shrink-0 rounded-full bg-[#291B2D] text-[#FCFAF6] flex items-center justify-center">
                <ArrowRight className="h-5 w-5" />
              </span>
            </button>
          )}
        </motion.div>
      )}

      {/* Daily Affirmation — personalized by the AI coach, refreshes every 24h */}
      {aff && (
        <motion.div data-testid={AFFIRMATION.card}
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
          className="rounded-3xl bg-gradient-to-br from-[#7B4C74] to-[#4A2E4F] text-[#FCFAF6] p-7 lg:p-9">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E7C98F] font-semibold flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Today's affirmation
            </p>
            <button data-testid={AFFIRMATION.viewSaved} onClick={() => navigate("/affirmations")}
              className="text-xs text-[#FCFAF6]/70 hover:text-[#FCFAF6] transition-smooth flex items-center gap-1.5">
              <Bookmark className="h-3.5 w-3.5" /> Saved
            </button>
          </div>
          <p className="font-display text-2xl lg:text-3xl leading-snug mt-4 max-w-2xl">"{aff.text}"</p>
          <div className="flex items-center gap-3 mt-6">
            <button data-testid={AFFIRMATION.save} onClick={toggleSaveAff}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-smooth ${
                aff.saved ? "bg-[#FCFAF6] text-[#4A2E4F]" : "bg-white/15 text-[#FCFAF6] hover:bg-white/25"
              }`}>
              <Heart className={`h-4 w-4 ${aff.saved ? "fill-current" : ""}`} /> {aff.saved ? "Saved" : "Save"}
            </button>
            <button data-testid={AFFIRMATION.share} onClick={shareAff}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium bg-white/15 text-[#FCFAF6] hover:bg-white/25 transition-smooth">
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>
        </motion.div>
      )}

      {/* Primary CTA — daily check-in (Brotherhood home_hero: massive high-contrast card) */}
      <motion.button data-testid={HOME.wellnessSnapshot}
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        onClick={() => navigate("/wellness")}
        className="w-full text-left rounded-3xl bg-[#291B2D] text-[#FCFAF6] p-7 lg:p-9 hover:-translate-y-0.5 transition-all shadow-[0_10px_40px_rgba(41,27,45,0.18)] group">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Start here</p>
            <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-2">Take today's check-in</h2>
            <p className="text-[#F7F2EA]/80 mt-2 max-w-md">A quiet minute with yourself — notice how you're really doing.</p>
          </div>
          <span className="h-14 w-14 shrink-0 rounded-full bg-[#C89B55] text-[#291B2D] flex items-center justify-center group-hover:scale-105 transition-transform">
            <ArrowRight className="h-6 w-6" />
          </span>
        </div>
      </motion.button>

      {/* Today's Focus */}
      {rec && (
        <div data-testid={HOME.recommendation}
          className="relative overflow-hidden rounded-3xl bg-white border border-[#291B2D]/5 p-7 lg:p-8 shadow-[0_4px_20px_rgba(41,27,45,0.04)]">
          <img src={HERO} alt="" className="absolute right-0 top-0 h-full w-1/3 object-cover opacity-15 hidden md:block" />
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#B97978] font-semibold">Today's focus</p>
            <h3 className="font-display text-2xl font-semibold mt-2">{rec.title}</h3>
            <p className="text-[#59535B] mt-2 max-w-lg leading-relaxed">{rec.summary}</p>
            <div className="flex items-center gap-4 mt-5">
              <button data-testid={HOME.startRecommendation} onClick={() => navigate(`/lesson/${rec.id}`)}
                className="rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] transition-all px-6 py-2.5 font-medium flex items-center gap-2">
                Begin lesson <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-sm text-[#8A838E] flex items-center gap-1.5"><Clock className="h-4 w-4" /> {rec.minutes} min</span>
            </div>
          </div>
        </div>
      )}

      {/* Growth plan strip */}
      <div className="bg-[#F7F2EA] border border-[#E5DED4] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium">Your growth plan</span>
          <span className="text-sm text-[#8A838E]">{stats?.lessons_completed ?? 0} of {stats?.total_lessons ?? 0} lessons</span>
        </div>
        <div className="h-2.5 bg-[#E5DED4] rounded-full overflow-hidden">
          <motion.div className="h-full bg-[#C89B55]" initial={{ width: 0 }} animate={{ width: `${pct}%` }}
            transition={{ duration: 1, ease: "easeOut" }} />
        </div>
      </div>

      {/* Quick-action tiles (Brotherhood: 7 icon tiles w/ accent tints) */}
      <div>
        <h3 className="font-display text-xl font-semibold mb-4">Explore</h3>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-3">
          {QUICK.map(({ key, label, icon: Icon, to }, i) => {
            const a = ACCENTS[key];
            return (
              <motion.button key={key} data-testid={`home-quick-${key}`} onClick={() => navigate(to)}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.04 }}
                className="flex flex-col items-center gap-2 group">
                <span className="h-14 w-14 rounded-2xl flex items-center justify-center transition-all group-hover:-translate-y-0.5 group-hover:shadow-md"
                  style={{ backgroundColor: a.bg }}>
                  <Icon className="h-6 w-6" style={{ color: a.fg }} strokeWidth={1.9} />
                </span>
                <span className="text-xs text-[#59535B] font-medium">{label}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Two ways to grow — mirrors the Sisterhood+ philosophy on the member home */}
      <div data-testid="home-philosophy">
        <h3 className="font-display text-xl font-semibold">Two ways to grow</h3>
        <p className="text-sm text-[#8A838E] mt-1 mb-4">Womanhood connects us — and our stories aren't identical.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button data-testid="home-connects" onClick={() => navigate("/learn")}
            className="text-left rounded-3xl bg-white border border-[#E5DED4] p-7 hover:-translate-y-0.5 hover:shadow-md transition-all group">
            <span className="inline-flex h-11 w-11 rounded-2xl bg-[#F1EDE0] text-[#C89B55] items-center justify-center mb-4">
              <HeartHandshake className="h-5 w-5" strokeWidth={1.8} />
            </span>
            <h4 className="font-display text-lg font-semibold">Explore what connects us</h4>
            <p className="text-sm text-[#59535B] mt-2 leading-relaxed">Growth for the experiences women share — confidence, boundaries, motherhood, money, rest.</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#291B2D] group-hover:gap-2.5 transition-all">Browse courses <ArrowRight className="h-4 w-4" /></span>
          </button>
          <button data-testid="home-shapes" onClick={() => navigate("/learn")}
            className="text-left rounded-3xl bg-[#291B2D] text-[#FCFAF6] p-7 hover:-translate-y-0.5 hover:shadow-md transition-all group">
            <span className="inline-flex h-11 w-11 rounded-2xl bg-white/10 text-[#C89B55] items-center justify-center mb-4">
              <Users className="h-5 w-5" strokeWidth={1.8} />
            </span>
            <h4 className="font-display text-lg font-semibold">Explore what shapes us</h4>
            <p className="text-sm text-[#F7F2EA]/75 mt-2 leading-relaxed">Culturally responsive collections for particular communities and lived experiences — open to all.</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#C89B55] group-hover:gap-2.5 transition-all">Browse collections <ArrowRight className="h-4 w-4" /></span>
          </button>
        </div>
      </div>

      {/* AI recommendation + reflection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button data-testid={HOME.coachShortcut} onClick={() => navigate("/coach")}
          className="text-left rounded-2xl p-6 border transition-all hover:-translate-y-0.5 hover:shadow-md"
          style={{ backgroundColor: ACCENTS.coach.bg, borderColor: ACCENTS.coach.fg + "22" }}>
          <MessageCircle className="h-6 w-6 mb-3" style={{ color: ACCENTS.coach.fg }} strokeWidth={1.75} />
          <h3 className="font-display text-lg font-semibold">A word from your coach</h3>
          <p className="text-[#59535B] text-sm mt-2 leading-relaxed">
            Whatever you're carrying today, you don't have to sort it out alone. Ask me anything.
          </p>
        </button>

        <button data-testid={HOME.promptCard} onClick={() => navigate("/journal")}
          className="text-left rounded-2xl p-6 border transition-all hover:-translate-y-0.5 hover:shadow-md"
          style={{ backgroundColor: ACCENTS.journal.bg, borderColor: ACCENTS.journal.fg + "22" }}>
          <NotebookPen className="h-6 w-6 mb-3" style={{ color: ACCENTS.journal.fg }} strokeWidth={1.75} />
          <h3 className="font-display text-lg font-semibold">Reflection of the day</h3>
          <p className="text-[#59535B] text-sm mt-2 leading-relaxed line-clamp-3">
            {data?.prompt_of_day?.text || "Take a quiet moment to write."}
          </p>
        </button>
      </div>

      {/* Daily encouragement — serif quote */}
      {data?.encouragement && (
        <div className="rounded-3xl bg-[#352238] text-[#FCFAF6] p-8 lg:p-10 text-center">
          <Sparkles className="h-6 w-6 text-[#C89B55] mx-auto mb-4" />
          <p className="font-display text-2xl lg:text-3xl leading-snug max-w-2xl mx-auto">"{data.encouragement}"</p>
        </div>
      )}

      {/* Recent wins */}
      <div>
        <h3 className="font-display text-xl font-semibold mb-4">Recent wins</h3>
        {data?.recent_wins?.length ? (
          <div className="space-y-3">
            {data.recent_wins.map((w) => (
              <div key={w.id} className="flex items-center gap-3 bg-white border border-[#E5DED4] rounded-xl p-4">
                <span className="h-8 w-8 rounded-full bg-[#ECF3EE] text-[#3D6B52] flex items-center justify-center">
                  <Check className="h-4 w-4" />
                </span>
                <span className="font-medium">{w.title}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-10 text-center bg-white rounded-3xl border border-[#291B2D]/5">
            <span className="w-14 h-14 rounded-full bg-[#F1EDE0] text-[#C89B55] flex items-center justify-center mb-3">
              <Sparkles className="h-6 w-6" />
            </span>
            <p className="font-display text-lg">Your first win is one lesson away.</p>
            <p className="text-sm text-[#8A838E] mt-1 max-w-sm">Complete a lesson and it'll show up here as a small, real victory.</p>
          </div>
        )}
      </div>
    </div>
  );
}
